import { useCallback } from "react"

import {
  SimulationWrapperType,
  type SimulationWrapperConfig,
} from "@/test-set/simulation-wrappers"

import { useTestSetEditorContext } from "../contexts/TestSetEditorContext"
import { EVENTS, useEventDispatcher } from "../event-bus"
import { getTestSetSimulationWrapperConfig } from "../utils/testSetSimulationWrapperConfig"
import { useTestSetSimulationWrapper } from "./useTestSetSimulationWrapper"

export function useTestSetSimulationWrapperConfig() {
  const dispatch = useEventDispatcher()
  const { featureFlags, metadata, testCases } = useTestSetEditorContext()
  const simulationWrapper = useTestSetSimulationWrapper()

  const handleSimulationWrapperChange = useCallback(
    (type: SimulationWrapperType | null) => {
      dispatch(EVENTS.METADATA_UPDATE, {
        simulationWrapperType: type,
        simulationWrapperTopLevelConfig:
          type === metadata.simulationWrapperType
            ? metadata.simulationWrapperTopLevelConfig
            : getTestSetSimulationWrapperConfig(type, testCases),
      })
    },
    [dispatch, metadata, testCases],
  )

  const handleSimulationWrapperConfigChange = useCallback(
    (config: SimulationWrapperConfig) => {
      if (simulationWrapper?.getType() === SimulationWrapperType.API_WRAPPER) {
        dispatch(EVENTS.API_SETUP_APPLY, { config })
      } else {
        dispatch(EVENTS.METADATA_UPDATE, {
          simulationWrapperTopLevelConfig: config,
        })
      }
    },
    [dispatch, simulationWrapper],
  )

  const hasEnabledSimulationWrapper =
    featureFlags.isCustomWrapperObservationsEnabled ||
    featureFlags.isApiSimulationWrapperEnabled
  const isSelectedSimulationWrapperEnabled =
    (simulationWrapper?.getType() === SimulationWrapperType.CUSTOM &&
      featureFlags.isCustomWrapperObservationsEnabled) ||
    (simulationWrapper?.getType() === SimulationWrapperType.API_WRAPPER &&
      featureFlags.isApiSimulationWrapperEnabled)

  return {
    hasEnabledSimulationWrapper,
    handleSimulationWrapperChange,
    handleSimulationWrapperConfigChange,
    isSelectedSimulationWrapperEnabled,
    simulationWrapper,
    simulationWrapperConfig:
      metadata.simulationWrapperTopLevelConfig ??
      getTestSetSimulationWrapperConfig(
        simulationWrapper?.getType(),
        testCases,
      ),
  }
}
